import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api } from "../lib/api";
import { BookOpen, Plus, X, Upload, Search } from "lucide-react";

/**
 * Picker for reusable manuals.
 *
 * Props:
 *   kind: "dpi" | "scaffold"
 *   value: string[]  (array of manual ids currently selected)
 *   onChange: (newIds: string[]) => void
 */
export default function ManualPicker({ kind, value = [], onChange }) {
  const [all, setAll] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadOpen, setUploadOpen] = useState(false);
  const [picking, setPicking] = useState(false);

  const reload = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/manuals?kind=${kind}`);
      setAll(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [kind]);

  const selected = (value || []).map((id) => all.find((m) => m.id === id)).filter(Boolean);
  const available = all.filter((m) => !(value || []).includes(m.id));

  const add = (id) => {
    if ((value || []).includes(id)) return;
    onChange([...(value || []), id]);
    setPicking(false);
  };
  const remove = (id) => {
    onChange((value || []).filter((v) => v !== id));
  };

  return (
    <div data-testid={`manual-picker-${kind}`}>
      <div className="text-[10px] uppercase tracking-wider text-neutral-500 mb-1 font-bold">
        Manuali allegati ({selected.length})
      </div>
      <div className="space-y-1.5 mb-2">
        {selected.map((m) => (
          <div key={m.id} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-neutral-50 border border-neutral-200">
            <BookOpen className="w-4 h-4 text-[#ff5a00] shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium truncate">{m.title}</div>
              <div className="text-[10px] text-neutral-500 truncate">{m.filename}</div>
            </div>
            <button
              type="button"
              onClick={() => remove(m.id)}
              className="text-neutral-500 hover:text-[#ff3b30] shrink-0"
              title="Rimuovi"
              data-testid={`manual-picker-remove-${m.id}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex gap-2 flex-wrap">
        <button
          type="button"
          onClick={() => setPicking(true)}
          disabled={loading || available.length === 0}
          className="bru-btn bru-btn-secondary !py-1.5 !px-3 text-xs disabled:opacity-50"
          data-testid={`manual-picker-add-${kind}`}
        >
          <Plus className="w-4 h-4" />
          Scegli dal deposito {available.length > 0 ? `(${available.length})` : ""}
        </button>
        <button
          type="button"
          onClick={() => setUploadOpen(true)}
          className="bru-btn bru-btn-secondary !py-1.5 !px-3 text-xs"
          data-testid={`manual-picker-upload-${kind}`}
        >
          <Upload className="w-4 h-4" />
          Carica nuovo
        </button>
      </div>

      {picking && (
        <PickDialog
          available={available}
          onClose={() => setPicking(false)}
          onPick={add}
        />
      )}
      {uploadOpen && (
        <QuickUpload
          kind={kind}
          onClose={() => setUploadOpen(false)}
          onDone={async (newId) => {
            setUploadOpen(false);
            await reload();
            if (newId) onChange([...(value || []), newId]);
          }}
        />
      )}
    </div>
  );
}

function PickDialog({ available, onClose, onPick }) {
  const [q, setQ] = useState("");
  if (typeof document === "undefined") return null;
  // Case-insensitive substring match on title + filename. Trim whitespace so
  // stray spaces from copy-paste don't zero out the result set.
  const needle = q.trim().toLowerCase();
  const filtered = needle
    ? available.filter((m) =>
        `${m.title || ""} ${m.filename || ""}`.toLowerCase().includes(needle)
      )
    : available;
  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className="bru-card w-full max-w-md p-5 max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <h3 className="text-lg font-bold mb-3">Seleziona manuale</h3>
        <div className="relative mb-3">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400 pointer-events-none" />
          <input
            type="search"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca per titolo o nome file…"
            className="bru-input !pl-9"
            autoFocus
            data-testid="manual-pick-search"
          />
        </div>
        {available.length === 0 ? (
          <div className="text-neutral-500 text-sm">Nessun manuale disponibile nel deposito.</div>
        ) : filtered.length === 0 ? (
          <div className="text-neutral-500 text-sm" data-testid="manual-pick-empty-search">
            Nessun risultato per &quot;{q}&quot;. Prova con un&apos;altra parola chiave.
          </div>
        ) : (
          <div className="space-y-1.5 overflow-y-auto -mx-1 px-1" data-testid="manual-pick-list">
            {filtered.map((m) => (
              <button
                key={m.id}
                type="button"
                onClick={() => onPick(m.id)}
                className="w-full text-left px-3 py-2 rounded-lg border border-neutral-200 hover:border-black hover:bg-neutral-50"
                data-testid={`manual-pick-${m.id}`}
              >
                <div className="text-sm font-medium">{m.title}</div>
                <div className="text-[10px] text-neutral-500">{m.filename}</div>
              </button>
            ))}
          </div>
        )}
        <div className="flex justify-end mt-4">
          <button type="button" onClick={onClose} className="bru-btn bru-btn-secondary">Chiudi</button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

function QuickUpload({ kind, onClose, onDone }) {
  const [title, setTitle] = useState("");
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    // CRITICAL: stop propagation so the synthetic submit event does NOT
    // bubble through the React tree to a parent <form> (the ScaffoldDialog
    // wraps its content in a form — without this, clicking "Carica e
    // seleziona" would inadvertently submit/close the outer scaffold dialog).
    e.stopPropagation();
    if (!file) { setErr("Seleziona un file"); return; }
    if (!title.trim()) { setErr("Titolo obbligatorio"); return; }
    setLoading(true); setErr("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("kind", kind);
      fd.append("title", title.trim());
      const { data } = await api.post("/manuals", fd);
      onDone(data?.id);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Errore caricamento");
    } finally {
      setLoading(false);
    }
  };

  // Render via portal to escape any parent modal's stopPropagation / backdrop
  // close behavior. Critical on mobile where the native file picker dismissal
  // can synthesize a phantom click that otherwise bubbles up and closes the
  // entire scaffolding dialog "sotto" il QuickUpload.
  if (typeof document === "undefined") return null;
  return createPortal(
    <div
      className="fixed inset-0 z-[60] bg-black/50 flex items-center justify-center p-4"
      onClick={onClose}
      onMouseDown={(e) => e.stopPropagation()}
      data-testid="manual-quickupload-backdrop"
    >
      <div
        className="bru-card w-full max-w-md p-5"
        onClick={(e) => e.stopPropagation()}
        onMouseDown={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-bold mb-3">Carica manuale ({kind === "dpi" ? "DPI" : "Scaffalature"})</h3>
        <form onSubmit={submit} className="space-y-3">
          <div>
            <label className="bru-label">Titolo *</label>
            <input
              className="bru-input"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Es. Manuale uso casco"
              disabled={loading}
              data-testid="manual-quickupload-title"
            />
          </div>
          <div>
            <label className="bru-label">File (PDF/immagine, max 30 MB) *</label>
            {/* Hidden native input + visible label: bypasses iOS quirks where a
                styled `<input type=file>` inside a modal may dismiss its
                parent dialog on file-picker return. */}
            <label
              className={`bru-btn bru-btn-secondary cursor-pointer ${loading ? "opacity-60 pointer-events-none" : ""}`}
              data-testid="manual-quickupload-file-label"
            >
              <Upload className="w-4 h-4" />
              {file ? "Cambia file" : "Scegli file"}
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png,.webp,.heic"
                onChange={(e) => {
                  const f = e.target.files?.[0] || null;
                  setFile(f);
                  e.target.value = "";
                }}
                className="hidden"
                data-testid="manual-quickupload-file-input"
              />
            </label>
            {file && (
              <div className="mt-2 text-xs text-neutral-700 break-all" data-testid="manual-quickupload-file-name">
                <b>Selezionato:</b> {file.name} · {Math.round((file.size || 0) / 1024)} KB
              </div>
            )}
          </div>
          {err && (
            <div className="text-sm text-[#ff3b30] border-[1.5px] border-[#ff3b30] bg-[#fff0f0] px-3 py-2 rounded" data-testid="manual-quickupload-error">
              {err}
            </div>
          )}
          {loading && (
            <div className="text-sm text-neutral-700 border-[1.5px] border-black bg-[#fff7f0] px-3 py-2 rounded" data-testid="manual-quickupload-loading">
              Caricamento in corso… non chiudere la pagina.
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="bru-btn bru-btn-secondary disabled:opacity-60"
              data-testid="manual-quickupload-cancel"
            >
              Annulla
            </button>
            <button
              type="submit"
              disabled={loading || !file || !title.trim()}
              className="bru-btn bru-btn-primary disabled:opacity-60"
              data-testid="manual-quickupload-submit"
            >
              {loading ? "Caricamento…" : "Carica e seleziona"}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body,
  );
}
